import { array, boolean, lazy, object, SchemaOf, string } from 'yup'
import {
  Application,
  Epic,
  FormProjectField,
  Importance,
  IntegrationConfig,
  IssueType,
  NgBugtrackerMaintenanceWindowRequest,
  Project,
  ProjectUser,
  Rule,
  ServerlessApiAccount
} from '../api/shared'

export interface JiraIntegrationForm {
  name: string
  credentials?: {
    name: string
    username: string
    password?: string
    host: string
    credentials_update?: boolean
  }
  credentials_id?: string
  applications: string[] | Application[]
  all_applications: boolean
  importance: string[] | Importance[]
  app_criterion: boolean
  auto_create_tickets_enabled: boolean
  auto_create_tickets_severities: string[]
  auto_create_tickets_rules: Rule[]
  /** sometimes strings are stored, sometimes Projects, etc */
  project: string | Project
  epic_id?: string | Epic | null
  assignee: string | ProjectUser
  issue_type: string | IssueType
  priority_field_available: boolean | null
  priority_critical: string | null
  priority_high: string | null
  priority_medium: string | null
  priority_low: string | null
  priority_note: string | null
  fields: FormProjectField[]
  serverless_account_ids: string[] | ServerlessApiAccount[]
  serverless_categories: string[]
  serverless: boolean
  integrations: IntegrationConfig[]
  integration: boolean
  exclude_sensitive_info: boolean
  send_tags: boolean
  /** the url of the webhook if a new integration */
  url?: string
  maintenance_windows?: NgBugtrackerMaintenanceWindowRequest
}

// This object has to be replaced in the near future with an interface in ./shared or something better
export const formSchema: SchemaOf<JiraIntegrationForm> = object()
  .shape({
    name: string().required(),
    serverless: boolean(),
    credentials: object({
      name: string(),
      host: string().required(),
      username: string(),
      password: string()
    }).nullable(),
    credentials_id: string().nullable(),
    project: object().shape({
      id: string(),
      key: string(),
      name: string()
    }),
    issue_type: object().required(),
    assignee: object().shape({
      displayName: string(),
      active: boolean(),
      timeZone: string(),
      locale: string(),
      accountId: string(),
      key: string(),
      emailAddress: string(),
      name: string().nullable()
    }),
    fields: array().of(
      object().shape({
        _projectField: object(),
        type: string(),
        key: string().required(),
        value: lazy((value) => {
          return typeof value === 'object'
            ? object().when('_projectField.required', {
                is: true,
                then: (schema) => schema.required()
              })
            : string().when('_projectField.required', {
                is: true,
                then: (schema) => schema.required()
              })
        }),
        items: string().nullable()
      })
    ),
    integration: boolean(),
    integrations: array()
      .of(
        object()
          .shape({
            issue_status: object()
              .shape({
                name: string(),
                id: string(),
                statusCategory: object().shape({
                  key: string(),
                  colorName: string()
                })
              })
              .required(),
            issue_resolution: object().shape({ id: string(), name: string() }),
            trace_status: object()
              .shape({
                value: string()
                  .oneOf([
                    'NotAProblem',
                    'Remediated',
                    'Fixed',
                    'Confirmed',
                    'Suspicious',
                    'Reported',
                    'AutoRemediated'
                  ])
                  .required(),
                translationId: string(),
                label: string()
              })
              .required(),
            trace_substatus: object().shape({
              value: string().oneOf(['FP', 'URL', 'SC', 'EC', 'OT']),
              translationId: string(),
              text: string()
            })
          })
          .test(
            'NotAProblem needs reason',
            'Reason Required when Not a Problem',
            (value) => {
              if (value.trace_status?.value === 'NotAProblem') {
                return !!value.trace_substatus.value
              }
              return true
            }
          )
          .test(
            'Done type needs resolution',
            'Issue in end state requires resolution',
            (value) => {
              if (value.issue_status?.statusCategory?.key === 'done') {
                return !!value.issue_resolution.name
              }
              return true
            }
          )
      )
      .test(
        'No repeats',
        'Duplicate Two Way Integrations are not allowed',
        (values) => {
          const hashes = values?.map(
            (value) =>
              `${value.issue_status?.id}-${value.issue_resolution?.id}-${value.trace_status?.value}-${value.trace_substatus?.value}`
          )
          return new Set(hashes).size === values?.length
        }
      ),
    priority_field_available: boolean().nullable(),
    priority_critical: string().nullable(),
    priority_high: string().nullable(),
    priority_medium: string().nullable(),
    priority_low: string().nullable(),
    priority_note: string().nullable(),
    applications: array().when('serverless', {
      is: false,
      then: array().of(
        object().shape({
          app_id: string(),
          archived: boolean(),
          assess: boolean(),
          assessPending: boolean(),
          code: string(),
          code_shorthand: string(),
          created: string(),
          first_seen: string(),
          importance: string(),
          importance_description: string(),
          language: string(),
          last_reset: object(),
          last_seen: string(),
          master: boolean(),
          missingRequiredFields: string().nullable(),
          name: string(),
          notes: string(),
          override_url: string().nullable(),
          path: string(),
          policies: array().of(string()),
          protect: string().nullable(),
          roles: array().of(string()),
          short_name: string().nullable(),
          size: string(),
          size_shorthand: string(),
          status: string(),
          tags: array().of(string()),
          techs: array().of(string()),
          total_modules: string(),
          scoreGrade: string()
        })
      )
    }),
    importance: array().when('app_criterion', {
      is: false,
      then: array().min(1)
    }),
    all_applications: boolean(),
    app_criterion: boolean(),
    serverless_account_ids: array().when('serverless', {
      is: true,
      then: array().of(
        object().shape({
          accountId: string(),
          active: boolean(),
          name: string(),
          orgId: string(),
          provider: string(),
          isBlocked: boolean()
        })
      )
    }),
    serverless_categories: array().of(string()),
    auto_create_tickets_enabled: boolean(),
    auto_create_tickets_severities: array().of(string()),
    auto_create_tickets_rules: array().of(
      object().shape({ description: string(), name: string(), title: string() })
    ),
    url: string(),
    epic: object().shape({
      id: string(),
      key: string()
    })
  })
  .required()
